/*
 * Sizes of one model are separate products in this catalogue — "Alu Pro 5
 * Ceraforce Koekenpan 24 cm" and "... 28 cm" are two products, not two
 * variants. A customer landing on one of them had no way of knowing the other
 * existed. This row finds the siblings and puts them under the price.
 *
 * The match is made on the product title with its measurement stripped off,
 * narrowed to the same brand, so unrelated products that merely share a word
 * never sneak in. Nothing is hardcoded per product: the row appears wherever
 * siblings exist and stays hidden where they do not.
 */

const SIZE_SUFFIX = /\s*(\d+(?:[.,]\d+)?)\s*(cm|mm|ml|liter|ltr|inch|l|")\.?\s*$/i;

function measurement(title) {
  const match = String(title).match(SIZE_SUFFIX);
  if (!match) return null;
  const amount = parseFloat(match[1].replace(',', '.'));
  if (Number.isNaN(amount)) return null;
  return { amount: amount, unit: match[2].toLowerCase(), label: `${match[1]} ${match[2]}` };
}

function baseName(title) {
  return String(title).replace(SIZE_SUFFIX, '').trim();
}

class ProductSizes extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    this.dataset.loaded = 'true';

    const title = this.dataset.productTitle || '';
    const own = measurement(title);
    if (!own) return; // no measurement in the name, so there is nothing to group

    this.base = baseName(title);
    if (!this.base) return;

    this.load().catch(() => {
      /* A failed lookup must never cost the customer the buy button. */
    });
  }

  async load() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url =
      `${root}search/suggest.json?q=${encodeURIComponent(this.base)}` +
      '&resources[type]=product&resources[limit]=10' +
      '&resources[options][unavailable_products]=last';

    const response = await fetch(url);
    if (!response.ok) return;

    const data = await response.json();
    const found = ((data.resources || {}).results || {}).products || [];
    const vendor = (this.dataset.vendor || '').toLowerCase();
    const base = this.base.toLowerCase();

    const siblings = [];
    const seen = new Set();

    found.forEach((product) => {
      if (vendor && String(product.vendor || '').toLowerCase() !== vendor) return;
      if (baseName(product.title).toLowerCase() !== base) return;

      const size = measurement(product.title);
      if (!size) return;
      if (seen.has(product.url)) return;
      seen.add(product.url);

      siblings.push({
        url: product.url,
        label: size.label,
        amount: size.amount,
        available: product.available !== false,
        current: product.url.split('?')[0] === this.dataset.productUrl,
      });
    });

    if (siblings.length < 2) return; // a single size is not a choice

    siblings.sort((a, b) => a.amount - b.amount);
    this.render(siblings);
  }

  render(siblings) {
    const label = this.dataset.label;
    const items = siblings
      .map((size) => {
        const classes = [
          'product-sizes__chip',
          size.current ? 'product-sizes__chip--current' : '',
          size.available ? '' : 'product-sizes__chip--sold-out',
        ]
          .filter(Boolean)
          .join(' ');

        if (size.current) {
          return `<li><span class="${classes}" aria-current="true">${size.label}</span></li>`;
        }
        return `<li><a class="${classes}" href="${size.url}">${size.label}</a></li>`;
      })
      .join('');

    this.innerHTML =
      (label ? `<span class="product-sizes__label body2">${label}</span>` : '') +
      `<ul class="unstyle-ul product-sizes__list" role="list">${items}</ul>`;
    this.hidden = false;
  }
}

if (!customElements.get('product-sizes')) {
  customElements.define('product-sizes', ProductSizes);
}
